import { useState, useEffect } from 'react';
import { departmentsApi, formatApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/layout/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Pencil, Trash2, Building2, Loader2, Home } from 'lucide-react';
import { toast } from 'sonner';

export default function Departments() {
  const { isAdmin } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    services: '',
    is_active: true,
    home_service_available: false,
    home_service_notes: ''
  });

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      const response = await departmentsApi.list();
      setDepartments(response.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (dept = null) => {
    if (dept) {
      setEditingDept(dept);
      setFormData({
        name: dept.name,
        description: dept.description || '',
        services: dept.services?.join(', ') || '',
        is_active: dept.is_active,
        home_service_available: dept.home_service_available || false,
        home_service_notes: dept.home_service_notes || ''
      });
    } else {
      setEditingDept(null);
      setFormData({
        name: '',
        description: '',
        services: '',
        is_active: true,
        home_service_available: false,
        home_service_notes: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        name: formData.name,
        description: formData.description,
        services: formData.services.split(',').map(s => s.trim()).filter(Boolean),
        is_active: formData.is_active,
        home_service_available: formData.home_service_available,
        home_service_notes: formData.home_service_notes
      };

      if (editingDept) {
        await departmentsApi.update(editingDept.id, data);
        toast.success('Reparto aggiornato');
      } else {
        await departmentsApi.create(data);
        toast.success('Reparto creato');
      }
      
      setDialogOpen(false);
      loadDepartments();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (dept) => {
    if (!window.confirm(`Eliminare il reparto "${dept.name}"?`)) return;
    
    try {
      await departmentsApi.delete(dept.id);
      toast.success('Reparto eliminato');
      loadDepartments();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Layout>
      <PageHeader 
        title="Reparti" 
        subtitle="Gestisci i reparti e i servizi del centro medico"
        actions={
          isAdmin && (
            <Button 
              onClick={() => openDialog()} 
              className="bg-sky-500 hover:bg-sky-600"
              data-testid="add-department-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Reparto
            </Button>
          )
        }
      />

      <div className="content-area" data-testid="departments-list">
        <div className="table-container">
          <Table>
            <TableHeader className="table-header">
              <TableRow>
                <TableHead>Reparto</TableHead>
                <TableHead>Descrizione</TableHead>
                <TableHead>Servizi</TableHead>
                <TableHead>Stato</TableHead>
                {isAdmin && <TableHead className="text-right">Azioni</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                  </TableCell>
                </TableRow>
              ) : departments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    <Building2 className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    Nessun reparto trovato
                  </TableCell>
                </TableRow>
              ) : (
                departments.map((dept) => (
                  <TableRow key={dept.id} data-testid={`dept-row-${dept.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-sky-100 rounded-lg flex items-center justify-center">
                          <Building2 className="w-4 h-4 text-sky-600" />
                        </div>
                        <span className="font-medium text-slate-800">{dept.name}</span>
                        {dept.home_service_available && (
                          <span className="ml-2 inline-flex items-center gap-1 text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-medium">
                            <Home className="w-3 h-3" /> Domicilio
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600 max-w-xs truncate">
                      {dept.description || '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {dept.services?.slice(0, 3).map((s, i) => (
                          <span key={i} className="badge bg-slate-100 text-slate-600">
                            {s}
                          </span>
                        ))}
                        {dept.services?.length > 3 && (
                          <span className="badge bg-slate-100 text-slate-600">
                            +{dept.services.length - 3}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`badge ${dept.is_active ? 'badge-confirmed' : 'badge-cancelled'}`}>
                        {dept.is_active ? 'Attivo' : 'Inattivo'}
                      </span>
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDialog(dept)}
                          data-testid={`edit-dept-${dept.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(dept)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-dept-${dept.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="department-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              {editingDept ? 'Modifica Reparto' : 'Nuovo Reparto'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="name">Nome Reparto *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="es. Odontoiatria"
                required
                className="mt-1.5"
                data-testid="dept-name-input"
              />
            </div>
            <div>
              <Label htmlFor="description">Descrizione</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrizione del reparto..."
                className="mt-1.5"
                rows={3}
                data-testid="dept-description-input"
              />
            </div>
            <div>
              <Label htmlFor="services">Servizi (separati da virgola)</Label>
              <Input
                id="services"
                value={formData.services}
                onChange={(e) => setFormData({ ...formData, services: e.target.value })}
                placeholder="es. Pulizia dentale, Otturazione, Estrazione"
                className="mt-1.5"
                data-testid="dept-services-input"
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                data-testid="dept-active-switch"
              />
              <Label htmlFor="is_active">Reparto attivo</Label>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Switch
                  id="home_service"
                  checked={formData.home_service_available}
                  onCheckedChange={(checked) => setFormData({ ...formData, home_service_available: checked })}
                  data-testid="dept-home-service-switch"
                />
                <div className="flex items-center gap-2">
                  <Home className="w-4 h-4 text-emerald-600" />
                  <Label htmlFor="home_service" className="text-emerald-800 font-medium">Servizio a domicilio disponibile</Label>
                </div>
              </div>
              {formData.home_service_available && (
                <div>
                  <Label htmlFor="home_notes" className="text-xs text-emerald-700">Note servizio domicilio</Label>
                  <Input
                    id="home_notes"
                    value={formData.home_service_notes}
                    onChange={(e) => setFormData({ ...formData, home_service_notes: e.target.value })}
                    placeholder="Es. Disponibile solo mattina, copertura max 20km"
                    className="mt-1 text-sm"
                    data-testid="dept-home-notes-input"
                  />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Annulla
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                className="bg-sky-500 hover:bg-sky-600"
                data-testid="dept-submit-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  editingDept ? 'Aggiorna' : 'Crea Reparto'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
